import React, { useEffect, useMemo, useState, useCallback } from 'react';

import { Box, Paper, Typography } from '@mui/material';
import { BankingService } from 'services';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'stores';
import { BankingState, setBalances } from 'stores/banking';
import { IPaginationResponse } from 'config/axios';
import { ETransactionStatus, ITransaction } from 'common/transactions.interface';

import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';
import { EAccountType } from 'common/accounts.interface';
import { TransactionsTypeSelector } from './components/TransactionsTypeSelector';
import { TransactionActions } from './components/TransactionsActions';
import { TransactionTable } from './components/TransactionsTable';
import { DashboardBalanceHeader } from '../../components/DashboardBalanceHeader';

const bankingService = new BankingService();

const usePaginatedTransactions = (params: { accountType?: EAccountType; type: string; startDate: DateTime; endDate: DateTime }) => {
  const { enqueueSnackbar } = useSnackbar();

  const { accountType, type, startDate, endDate } = params;

  const [transactions, setTransactions] = useState<ITransaction[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  /** Load current page */
  const load = useCallback(async () => {
    if (!accountType) return;

    setLoading(true);

    try {
      const response: IPaginationResponse<ITransaction> = await bankingService.getTransactions({
        accountType,
        page,
        limit: 10,
        type: type === 'Todas' ? undefined : type,
        status: ETransactionStatus.CONFIRMED,
        startDate: `${startDate.toISODate()}T00:00:00`,
        endDate: `${endDate.toISODate()}T23:59:59`,
      });

      setTransactions((prev) => [...prev, ...response.items]);
      setHasMore(response.page * response.limit < response.total);
    } catch (err) {
      enqueueSnackbar('Erro ao carregar transações', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [accountType, page, type, startDate, endDate]);

  /** Reload from page 1 when filters change */
  useEffect(() => {
    setTransactions([]);
    setPage(1);
    setHasMore(true);
  }, [accountType, type, startDate.toISODate(), endDate.toISODate()]);

  /** Load whenever the page changes */
  useEffect(() => {
    if (!hasMore && page !== 1) return;
    load();
  }, [page, load]);

  const loadMore = () => {
    if (!loading && hasMore) setPage((p) => p + 1);
  };

  return {
    transactions,
    loading,
    hasMore,
    loadMore,
    page,
  };
};

const TransactionsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const state = location.state as { accountType?: EAccountType };

  const { accounts } = useSelector<IRootState, BankingState>(({ banking }) => banking);

  const [accountType, setAccountType] = useState<EAccountType>(state?.accountType || EAccountType.PAYMENT);

  // Filters
  const [type, setType] = useState('Todas');
  const [startDate, setStartDate] = useState(DateTime.now().minus({ days: 7 }));
  const [endDate, setEndDate] = useState(DateTime.now());

  const selectedAccount = useMemo(() => accounts?.find((a: any) => a.accountType === accountType), [accounts, accountType]);

  const { transactions, loading, hasMore, loadMore } = usePaginatedTransactions({
    accountType: selectedAccount?.accountType,
    type,
    startDate,
    endDate,
  });

  const loadBalances = useCallback(async () => {
    if (!accounts) return;

    try {
      const balances = await bankingService.getAccountsBalance();
      dispatch(setBalances(balances));
    } catch {
      enqueueSnackbar('Erro ao carregar saldos das contas', {
        variant: 'error',
      });
    }
  }, [accounts]);

  useEffect(() => {
    loadBalances();
  }, [accounts]);

  return (
    <>
      <TransactionsTypeSelector startDate={startDate} endDate={endDate} accountType={accountType} onAccountTypeChange={setAccountType} />

      <DashboardBalanceHeader account={selectedAccount} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h3" sx={{ fontWeight: 500, color: '#434241' }}>
          Extrato de movimentações
        </Typography>
      </Box>

      <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #F2F4F8' }} elevation={0}>
        <TransactionActions
          type={type}
          startDate={startDate}
          endDate={endDate}
          loading={loading}
          onTypeChange={setType}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />

        <TransactionTable transactions={transactions} loading={loading} hasMore={hasMore} onLoadMore={loadMore} />
      </Paper>
    </>
  );
};

export default TransactionsPage;
